import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Modal, Field, inputClass, PrimaryButton, EmptyState } from "../components/common";
import { toast } from "sonner";
import { CalendarBlank, Plus, Trash, Flag } from "@phosphor-icons/react";
import CronogramaGantt from "./CronogramaGantt";

const day = 86400000;
const parse = (d) => new Date(d + "T00:00:00");

export default function Programa() {
  const { user } = useAuth();
  const [tab, setTab] = useState("resumen");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", start_date: "", end_date: "" });
  const canManage = ["admin", "supervisor", "gerente_general", "gerente_area"].includes(user?.role);

  const load = () => api.get("/program").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addMs = async (e) => {
    e.preventDefault();
    try { await api.post("/program/milestones", form); toast.success("Hito agregado"); setOpen(false); setForm({ title: "", start_date: "", end_date: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delMs = async (id) => { try { await api.delete(`/program/milestones/${id}`); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const dates = items.flatMap((i) => [parse(i.start), parse(i.end)]);
  const min = dates.length ? new Date(Math.min(...dates)) : null;
  const max = dates.length ? new Date(Math.max(...dates)) : null;
  const span = min && max ? Math.max(1, (max - min) / day) : 1;
  const today = new Date();
  const todayPct = min && max && today >= min && today <= max ? ((today - min) / day / span) * 100 : null;

  const pos = (i) => {
    const s = ((parse(i.start) - min) / day / span) * 100;
    const w = Math.max(1.5, ((parse(i.end) - parse(i.start)) / day / span) * 100);
    return { left: `${s}%`, width: `${w}%` };
  };

  const TabBtn = ({ id, children }) => (
    <button onClick={() => setTab(id)} data-testid={`program-tab-${id}`}
      className={`px-4 h-10 rounded-sm text-sm font-semibold transition-colors ${tab === id ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
      {children}
    </button>
  );

  return (
    <div>
      <PageHeader overline="Planeación" title="Programa de obra"
        action={tab === "resumen" && canManage && <PrimaryButton onClick={() => setOpen(true)} data-testid="add-milestone-btn"><Plus size={18} weight="bold" /> Agregar hito</PrimaryButton>} />

      <div className="flex gap-2 mb-5">
        <TabBtn id="resumen">Resumen (contratos y órdenes)</TabBtn>
        <TabBtn id="cronograma">Cronograma (tareas)</TabBtn>
      </div>

      {tab === "cronograma" ? (
        <CronogramaGantt canManage={canManage} />
      ) : (
        <>
          <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-primary">
            El resumen se arma <b className="text-foreground">automáticamente</b> con las fechas de inicio y término de cada contrato y orden de trabajo. Para capturar tareas detalladas con predecesoras y grupos, usa la pestaña <b className="text-foreground">Cronograma (tareas)</b>.
          </Card>

          {items.length === 0 ? (
            <EmptyState icon={CalendarBlank} title="Sin fechas programadas" subtitle="Agrega fechas de inicio/término a tus contratos para verlos en el resumen." />
          ) : (
            <Card className="p-6 overflow-x-auto" data-testid="program-gantt">
              <div className="text-xs text-muted-foreground mb-4 flex justify-between" style={{ minWidth: 640 }}>
                <span>{min?.toLocaleDateString("es-MX")}</span>
                <span>{max?.toLocaleDateString("es-MX")}</span>
              </div>
              <div className="space-y-3 relative" style={{ minWidth: 640 }}>
                {todayPct != null && (
                  <div className="absolute top-0 bottom-0 w-px bg-red-500/70 z-10" style={{ left: `${todayPct}%` }} title="Hoy" />
                )}
                {items.map((i) => (
                  <div key={i.id} className="flex items-center gap-3" data-testid={`program-item-${i.id}`}>
                    <div className="w-44 flex-shrink-0 text-sm truncate">
                      <div className="font-medium flex items-center gap-1 flex-wrap">{i.type === "hito" && <Flag size={13} weight="fill" className="text-primary" />}{i.title}
                        {i.delayed && <span className="px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 text-[10px] font-bold" data-testid={`delayed-${i.id}`}>Atrasado</span>}
                      </div>
                      {i.contractor && <div className="text-xs text-muted-foreground truncate">{i.contractor}</div>}
                    </div>
                    <div className="flex-1 relative h-7 bg-muted rounded-sm">
                      {i.type === "contrato" && i.expected != null && i.expected > 0 && i.expected < 100 && (
                        <div className="absolute top-0 bottom-0 w-0.5 bg-slate-500/60 z-10" style={{ left: `calc(${parseFloat(pos(i).left)}% + ${(i.expected / 100) * parseFloat(pos(i).width)}%)` }} title={`Esperado ${i.expected}%`} />
                      )}
                      <div className={`absolute h-full rounded-sm ${i.type === "hito" ? "bg-slate-800" : i.delayed ? "bg-red-100" : "bg-primary/30"}`} style={pos(i)}>
                        {i.type === "contrato" && i.progress != null && (
                          <div className={`h-full rounded-sm flex items-center justify-end pr-1 ${i.delayed ? "bg-red-600" : "bg-primary"}`} style={{ width: `${Math.min(100, i.progress)}%` }}>
                            <span className="text-[9px] text-white font-bold">{i.progress}%</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {canManage && i.type === "hito" && <button onClick={() => delMs(i.id)} className="text-muted-foreground hover:text-danger"><Trash size={15} /></button>}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mt-5 pt-4 border-t border-border text-xs text-muted-foreground" style={{ minWidth: 640 }}>
                <span className="flex items-center gap-1.5"><span className="w-4 h-2 rounded-sm bg-primary inline-block" /> Contratos (avance)</span>
                <span className="flex items-center gap-1.5"><span className="w-4 h-2 rounded-sm bg-indigo-600 inline-block" /> Órdenes de trabajo</span>
                <span className="flex items-center gap-1.5"><span className="w-4 h-2 rounded-sm bg-slate-800 inline-block" /> Hito</span>
                <span className="flex items-center gap-1.5"><span className="w-4 h-2 rounded-sm bg-red-600 inline-block" /> Atrasado (bajo lo esperado)</span>
                <span className="flex items-center gap-1.5"><span className="w-0.5 h-3 bg-slate-500/60 inline-block" /> Avance esperado</span>
                <span className="flex items-center gap-1.5"><span className="w-0.5 h-3 bg-red-500/70 inline-block" /> Hoy</span>
              </div>
            </Card>
          )}
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Agregar hito al programa">
        <form onSubmit={addMs} className="space-y-4">
          <Field label="Título *"><input required data-testid="ms-title" className={inputClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej. Entrega de obra negra" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio *"><input required type="date" data-testid="ms-start" className={inputClass} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
            <Field label="Término *"><input required type="date" data-testid="ms-end" className={inputClass} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          </div>
          <PrimaryButton type="submit" data-testid="save-ms-btn">Agregar hito</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
